/**
 * The select and the slider (docs/design/visual-overhaul.md §7.22; WP3).
 *
 * `SelectBox` is a drop-in for `<select>`: the same props and the same
 * `<option>` / `<optgroup>` children. It draws a field-looking button and our
 * own listbox in the menu material (32px rows, a check on the chosen row,
 * at most 320 tall, type-ahead); the OS list is never shown. A hidden native
 * `<select>` stays beside the button and carries every prop, so forms, a
 * caller's `onChange(e => e.target.value)`, tests and the e2e (which set the
 * native value and fire `change`) work as before. Choosing a row sets the
 * native value and fires a real `change` event.
 *
 * It is self-contained (its own portal, placement and keys), so the app and
 * the Console share it: Escape is taken at the window, before either app's
 * overlay stack sees it, and closes only the list.
 */
import {
  Children,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type InputHTMLAttributes,
  type KeyboardEvent as RKeyboardEvent,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './icon';

interface Opt {
  value: string;
  label: string;
  disabled: boolean;
  group?: string;
}

function textOf(n: ReactNode): string {
  if (n === null || n === undefined || typeof n === 'boolean') return '';
  if (typeof n === 'string' || typeof n === 'number') return String(n);
  if (Array.isArray(n)) return n.map(textOf).join('');
  if (isValidElement(n)) return textOf((n.props as { children?: ReactNode }).children);
  return '';
}

/** The options a `<select>`'s children describe, groups flattened with their label. */
function optionsOf(children: ReactNode, group?: string): Opt[] {
  const out: Opt[] = [];
  for (const c of Children.toArray(children)) {
    if (!isValidElement(c)) continue;
    const el = c as ReactElement<{
      value?: string | number;
      children?: ReactNode;
      disabled?: boolean;
      label?: string;
    }>;
    if (el.type === 'option') {
      const label = textOf(el.props.children);
      out.push({
        value: el.props.value !== undefined ? String(el.props.value) : label,
        label,
        disabled: !!el.props.disabled,
        group,
      });
    } else if (el.type === 'optgroup') {
      out.push(...optionsOf(el.props.children, el.props.label ?? ''));
    } else if (el.props.children !== undefined) {
      // a fragment or wrapper around options
      out.push(...optionsOf(el.props.children, group));
    }
  }
  return out;
}

/** Sets a native control's value as a person would, so React's onChange runs. */
function setNative(el: HTMLSelectElement, value: string): void {
  const set = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value')?.set;
  if (set) set.call(el, value);
  else el.value = value;
  el.dispatchEvent(new Event('change', { bubbles: true }));
}

/** Whether exits can be watched (a real browser); jsdom closes at once. */
const animates = () =>
  typeof Element !== 'undefined' && typeof Element.prototype.getAnimations === 'function';

export type SelectBoxProps = SelectHTMLAttributes<HTMLSelectElement> & {
  /** Classes for the button (the field look: `nx-input`). */
  className?: string;
  style?: CSSProperties;
};

export function SelectBox({
  children,
  className,
  style,
  value,
  defaultValue,
  disabled,
  title,
  id,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  'aria-describedby': ariaDescribedby,
  'aria-invalid': ariaInvalid,
  ...rest
}: SelectBoxProps) {
  const opts = optionsOf(children);
  const native = useRef<HTMLSelectElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const uid = useId();
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [active, setActive] = useState(-1);
  // uncontrolled use follows the native control
  const [inner, setInner] = useState<string>(
    defaultValue !== undefined ? String(defaultValue) : (opts[0]?.value ?? ''),
  );
  const cur = value !== undefined && value !== null ? String(value) : inner;
  const chosen = opts.findIndex((o) => o.value === cur);
  const shown = chosen >= 0 ? opts[chosen]!.label : (opts[0]?.label ?? '');
  const typed = useRef({ s: '', at: 0 });

  const close = useCallback((refocus = true) => {
    setOpen(false);
    if (animates()) {
      setClosing(true);
      setTimeout(() => setClosing(false), 150);
    }
    if (refocus) btn.current?.focus({ preventScroll: true });
  }, []);

  const pick = (i: number) => {
    const o = opts[i];
    if (!o || o.disabled) return;
    close();
    if (o.value === cur) return;
    if (value === undefined) setInner(o.value);
    if (native.current) setNative(native.current, o.value);
  };

  const enabled = (i: number) => !!opts[i] && !opts[i]!.disabled;
  const step = (from: number, d: number) => {
    for (let k = 1; k <= opts.length; k++) {
      const i = (from + d * k + opts.length * 4) % opts.length;
      if (enabled(i)) return i;
    }
    return from;
  };
  const typeAhead = (key: string) => {
    const now = Date.now();
    const t = typed.current;
    t.s = now - t.at > 700 ? key : t.s + key;
    t.at = now;
    const s = t.s.toLowerCase();
    const start = Math.max(0, active);
    for (let k = 0; k < opts.length; k++) {
      const i = (start + (t.s.length === 1 ? k + 1 : k)) % opts.length;
      if (enabled(i) && opts[i]!.label.toLowerCase().startsWith(s)) return i;
    }
    return -1;
  };

  const openList = () => {
    if (disabled || !opts.length) return;
    setActive(chosen >= 0 && enabled(chosen) ? chosen : step(-1, 1));
    setClosing(false);
    setOpen(true);
  };

  // place under the button (flip above when there is no room), 4px off
  useLayoutEffect(() => {
    if (!open && !closing) return;
    const el = list.current;
    const b = btn.current;
    if (!el || !b) return;
    const placeIt = () => {
      const r = b.getBoundingClientRect();
      el.style.minWidth = Math.round(r.width) + 'px';
      const h = el.offsetHeight;
      const m = 8;
      const below = innerHeight - r.bottom - 4 - m;
      const up = below < h && r.top - 4 - m > below;
      const y = up ? Math.max(m, r.top - 4 - h) : Math.min(r.bottom + 4, innerHeight - m - h);
      const w = el.offsetWidth;
      const x = Math.max(m, Math.min(r.left, innerWidth - m - w));
      el.style.left = Math.round(x) + 'px';
      el.style.top = Math.round(Math.max(m, y)) + 'px';
      el.style.transformOrigin = up ? '50% 100%' : '50% 0';
    };
    placeIt();
    if (open) {
      el.focus({ preventScroll: true });
      el.querySelector<HTMLElement>('[aria-selected="true"]')?.scrollIntoView?.({
        block: 'nearest',
      });
    }
    addEventListener('resize', placeIt);
    return () => removeEventListener('resize', placeIt);
  }, [open, closing]);

  // the active row stays in view
  useEffect(() => {
    if (!open || active < 0) return;
    list.current
      ?.querySelector<HTMLElement>(`[data-i="${active}"]`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [open, active]);

  // keys and clicks while open: taken before any app's overlay stack
  useEffect(() => {
    if (!open) return;
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        close();
      } else if (e.key === 'Tab') {
        close(false);
      }
    };
    const down = (e: PointerEvent) => {
      const t = e.target as Node;
      if (list.current?.contains(t) || btn.current?.contains(t)) return;
      close(false);
    };
    const scroll = (e: Event) => {
      if (list.current?.contains(e.target as Node)) return;
      close(false);
    };
    addEventListener('keydown', key, true);
    addEventListener('pointerdown', down, true);
    addEventListener('scroll', scroll, true);
    return () => {
      removeEventListener('keydown', key, true);
      removeEventListener('pointerdown', down, true);
      removeEventListener('scroll', scroll, true);
    };
  }, [open, close]);

  const listId = uid + '-lb';
  const onListKey = (e: RKeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => step(a, 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => step(a < 0 ? 0 : a, -1));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActive(step(-1, 1));
    } else if (e.key === 'End') {
      e.preventDefault();
      setActive(step(opts.length, -1));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (active >= 0) pick(active);
    } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const i = typeAhead(e.key);
      if (i >= 0) setActive(i);
    }
  };

  let lastGroup: string | undefined;
  const rows: ReactNode[] = [];
  opts.forEach((o, i) => {
    if (o.group !== undefined && o.group !== lastGroup) {
      rows.push(
        <div key={'g' + i} className="nx-lb-h" role="presentation">
          {o.group}
        </div>,
      );
    }
    lastGroup = o.group;
    rows.push(
      <div
        key={i}
        id={`${uid}-o${i}`}
        data-i={i}
        className="nx-lb-o"
        role="option"
        aria-selected={i === chosen}
        aria-disabled={o.disabled || undefined}
        data-active={open && i === active ? '' : undefined}
        onPointerMove={() => enabled(i) && i !== active && setActive(i)}
        onClick={() => pick(i)}
      >
        <span className="t" dir="auto">
          {o.label}
        </span>
        {i === chosen ? <Icon name="check" /> : null}
      </div>,
    );
  });

  return (
    <>
      <button
        ref={btn}
        type="button"
        id={id}
        className={['nx-select-btn', className].filter(Boolean).join(' ')}
        style={style}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && active >= 0 ? `${uid}-o${active}` : undefined}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        aria-describedby={ariaDescribedby}
        aria-invalid={ariaInvalid}
        disabled={disabled}
        title={title ?? shown}
        onClick={() => (open ? close() : openList())}
        onKeyDown={(e) => {
          if (open) return;
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openList();
          }
        }}
      >
        <span className="v" dir="auto">
          {shown}
        </span>
        <Icon name="chev-down" />
      </button>
      <select
        ref={native}
        className="nx-select-native"
        tabIndex={-1}
        aria-hidden="true"
        value={value === undefined ? inner : (value ?? '')}
        disabled={disabled}
        {...rest}
        onChange={(e) => {
          if (value === undefined) setInner(e.target.value);
          rest.onChange?.(e);
        }}
      >
        {children}
      </select>
      {(open || closing) && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={list}
              id={listId}
              className="nx-lb"
              role="listbox"
              tabIndex={-1}
              aria-label={ariaLabel ?? shown}
              data-state={open ? 'open' : 'closed'}
              aria-activedescendant={open && active >= 0 ? `${uid}-o${active}` : undefined}
              onKeyDown={onListKey}
              onPointerLeave={() => setActive(-1)}
            >
              {rows}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

/** The fill of a slider as `--f` (0..1), for `.nx-range`. */
export function rangeFill(value: number, min = 0, max = 100): CSSProperties {
  const f = max > min ? (value - min) / (max - min) : 0;
  return { '--f': Math.max(0, Math.min(1, f)).toFixed(4) } as CSSProperties;
}

/** The kit slider (§7.22): a 6px track, the accent fill, a 16px thumb. */
export function Range({
  className,
  style,
  value,
  min = 0,
  max = 100,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { value: number }) {
  return (
    <input
      type="range"
      className={['nx-range', className].filter(Boolean).join(' ')}
      value={value}
      min={min}
      max={max}
      style={{ ...rangeFill(value, Number(min), Number(max)), ...style }}
      {...rest}
    />
  );
}
